import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, ChevronLeft, ChevronRight, Star, ChevronDown, Check, Calendar, Clock } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import QuestIllustration from './QuestIllustration';
import XPPopup from './XPPopup';
import { getThemeColors } from '../../../utils/themeConfig';

// Custom hook for XP management
const useXPManagement = () => {
  const [totalXP, setTotalXP] = useState(0);
  const [questXP, setQuestXP] = useState(0);
  const [showXPPopup, setShowXPPopup] = useState(false);
  const [currentQuestionXP, setCurrentQuestionXP] = useState(0);

  const addXP = useCallback((xp) => {
    setCurrentQuestionXP(xp);
    setQuestXP(prev => prev + xp);
    setTotalXP(prev => prev + xp);
    setShowXPPopup(true);
  }, []);

  const resetQuestXP = useCallback(() => {
    setQuestXP(0);
  }, []);

  return { 
    totalXP, 
    questXP, 
    showXPPopup, 
    currentQuestionXP, 
    addXP, 
    resetQuestXP,
    setShowXPPopup 
  };
};

// Custom hook for quest progress
const useQuestProgress = (quests, currentQuestIndex, currentQuestionIndex) => {
  const currentQuest = useMemo(() => 
    quests[currentQuestIndex], [quests, currentQuestIndex]
  );

  const currentQuestion = useMemo(() => 
    currentQuest?.questions?.[currentQuestionIndex], 
    [currentQuest, currentQuestionIndex]
  );

  const isLastQuestionInQuest = useMemo(() => 
    currentQuestionIndex === (currentQuest?.questions?.length || 0) - 1,
    [currentQuestionIndex, currentQuest]
  );

  const isLastQuest = useMemo(() => 
    currentQuestIndex === quests.length - 1,
    [currentQuestIndex, quests.length]
  );

  return {
    currentQuest,
    currentQuestion,
    isLastQuestionInQuest,
    isLastQuest
  };
};

// Input Components
const TextInput = React.memo(({ value, onChange, theme, placeholder, type = 'text', rows = 1 }) => {
  const commonProps = {
    value: value || '',
    onChange: (e) => onChange(e.target.value),
    placeholder,
    className: "w-full px-5 py-4 border-2 rounded-xl focus:outline-none text-base sm:text-lg transition-all focus:ring-4 focus:ring-blue-100",
    style: {
      borderColor: '#E5E7EB',
      backgroundColor: '#FFFFFF',
      color: '#1F2937'
    },
    onFocus: (e) => {
      e.target.style.borderColor = '#3B82F6';
      e.target.style.backgroundColor = '#FFFFFF';
    },
    onBlur: (e) => {
      e.target.style.borderColor = '#E5E7EB';
    }
  };

  if (rows > 1) {
    return <textarea {...commonProps} rows={rows} />;
  }

  return <input type={type} {...commonProps} />;
});

const MultipleChoiceInput = React.memo(({ options, value, onChange, theme, allowOther = true }) => {
  const [otherValue, setOtherValue] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);

  useEffect(() => {
    // Check if current value is not in options (meaning it's an "Other" response)
    if (value && !options?.includes(value) && value !== 'Other') {
      setOtherValue(value);
      setShowOtherInput(true);
    }
  }, [value, options]);

  const handleOptionSelect = (option) => {
    if (option === 'Other') {
      setShowOtherInput(true);
      if (otherValue) onChange(otherValue);
    } else {
      setShowOtherInput(false);
      setOtherValue('');
      onChange(option);
    }
  };

  const handleOtherChange = (val) => {
    setOtherValue(val);
    onChange(val);
  };

  const allOptions = allowOther && !options?.includes('Other')
    ? [...(options || []), 'Other']
    : options;

  return (
    <div className="space-y-3">
      {allOptions?.map((option, idx) => {
        const isSelected = showOtherInput && option === 'Other' ? true : value === option;
        return (
          <motion.button
            key={`${option}-${idx}`}
            onClick={() => handleOptionSelect(option)}
            whileHover={{ scale: 1.02, x: 4 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            className="w-full text-left px-5 py-4 border-2 rounded-xl transition-all font-medium text-base sm:text-lg relative overflow-hidden hover:border-blue-400"
            style={{
              borderColor: isSelected ? '#3B82F6' : '#E5E7EB',
              backgroundColor: isSelected ? '#EFF6FF' : '#FFFFFF',
              color: isSelected ? '#1E40AF' : '#374151'
            }}
          >
            {isSelected && (
              <motion.div
                layoutId="selectedOption"
                className="absolute inset-0 bg-blue-50 -z-10"
                initial={false}
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
            <span className="relative z-10">{option}</span>
          </motion.button>
        );
      })}

      {showOtherInput && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <input
            type="text"
            value={otherValue}
            onChange={(e) => handleOtherChange(e.target.value)}
            placeholder="Please specify..."
            autoFocus
            className="w-full px-5 py-4 border-2 border-blue-400 rounded-xl focus:outline-none text-base sm:text-lg transition-all focus:ring-4 focus:ring-blue-100"
            style={{
              backgroundColor: '#FFFFFF',
              color: '#1F2937'
            }}
          />
        </motion.div>
      )}
    </div>
  );
});

const CheckboxInput = React.memo(({ options, value, onChange, theme, allowOther = true }) => {
  const checkboxAnswers = value || [];
  const [otherValue, setOtherValue] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);

  // Initialize other value if it exists in the answers
  useEffect(() => {
    const otherAnswers = checkboxAnswers.filter(answer => !options?.includes(answer));
    if (otherAnswers.length > 0) {
      setOtherValue(otherAnswers[0]);
      setShowOtherInput(true);
    }
  }, [checkboxAnswers, options]);

  const handleCheckboxChange = (option, checked) => {
    if (option === 'Other') {
      setShowOtherInput(checked);
      if (!checked) {
        // Remove "Other" and any custom other values
        const newAnswers = checkboxAnswers.filter(item =>
          item !== 'Other' && options?.includes(item)
        );
        onChange(newAnswers);
        setOtherValue('');
      } else if (otherValue) {
        // Add the custom other value
        const newAnswers = [...checkboxAnswers.filter(item => item !== 'Other'), otherValue];
        onChange(newAnswers);
      }
    } else {
      const newAnswers = checked
        ? [...checkboxAnswers, option]
        : checkboxAnswers.filter(item => item !== option);
      onChange(newAnswers);
    }
  };

  const handleOtherValueChange = (val) => {
    setOtherValue(val);
    // Update the answers with the new "Other" value
    const newAnswers = [
      ...checkboxAnswers.filter(item => options?.includes(item)),
      val
    ].filter(item => item); // Remove empty strings
    onChange(newAnswers);
  };

  const allOptions = allowOther && !options?.includes('Other')
    ? [...(options || []), 'Other']
    : options;

  return (
    <div className="space-y-3">
      {allOptions?.map((option, idx) => {
        const isChecked = option === 'Other'
          ? showOtherInput
          : checkboxAnswers.includes(option);
        return (
          <motion.label
            key={idx}
            whileHover={{ scale: 1.01, x: 4 }}
            whileTap={{ scale: 0.99 }}
            className="flex items-center gap-4 p-4 border-2 rounded-xl transition-all cursor-pointer hover:border-blue-400"
            style={{
              borderColor: isChecked ? '#3B82F6' : '#E5E7EB',
              backgroundColor: isChecked ? '#EFF6FF' : '#FFFFFF'
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => handleCheckboxChange(option, e.target.checked)}
              className="w-5 h-5 rounded-md accent-blue-600 cursor-pointer"
            />
            <span
              className="font-medium text-base sm:text-lg flex-1"
              style={{ color: isChecked ? '#1E40AF' : '#374151' }}
            >
              {option}
            </span>
          </motion.label>
        );
      })}

      {showOtherInput && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden ml-9"
        >
          <input
            type="text"
            value={otherValue}
            onChange={(e) => handleOtherValueChange(e.target.value)}
            placeholder="Please specify..."
            autoFocus
            className="w-full px-5 py-4 border-2 border-blue-400 rounded-xl focus:outline-none text-base sm:text-lg transition-all focus:ring-4 focus:ring-blue-100"
            style={{
              backgroundColor: '#FFFFFF',
              color: '#1F2937'
            }}
          />
        </motion.div>
      )}
    </div>
  );
});

const RatingInput = React.memo(({ value, onChange, theme }) => (
  <div className="flex gap-3 justify-center">
    {[1, 2, 3, 4, 5].map((star) => {
      const isSelected = value && star <= value;
      return (
        <motion.button
          key={star}
          onClick={() => onChange(star)}
          whileHover={{ scale: 1.15, rotate: isSelected ? 0 : 12 }}
          whileTap={{ scale: 0.9 }}
          className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-xl sm:text-2xl border-2 rounded-xl transition-all hover:border-blue-500"
          style={{
            borderColor: isSelected ? '#3B82F6' : '#E5E7EB',
            backgroundColor: isSelected ? '#EFF6FF' : '#FFFFFF',
            color: isSelected ? '#3B82F6' : '#9CA3AF'
          }}
        >
          <motion.div
            animate={{ scale: isSelected ? [1, 1.2, 1] : 1 }}
            transition={{ duration: 0.3 }}
          >
            <Star
              size={24}
              fill={isSelected ? "#3B82F6" : "none"}
              strokeWidth={2}
            />
          </motion.div>
        </motion.button>
      );
    })}
  </div>
));

const ImageInput = React.memo(({ options, value, onChange, theme }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
    {options?.map((option, idx) => {
      const isSelected = value === option;
      return (
        <motion.button
          key={idx}
          onClick={() => onChange(option)}
          whileHover={{ scale: 1.05, y: -4 }}
          whileTap={{ scale: 0.95 }}
          className="aspect-square border-3 rounded-2xl overflow-hidden transition-all relative hover:border-blue-500"
          style={{
            borderColor: isSelected ? '#3B82F6' : '#E5E7EB',
            borderWidth: isSelected ? '3px' : '2px'
          }}
        >
          {isSelected && (
            <motion.div
              layoutId="selectedImage"
              className="absolute inset-0 ring-4 ring-blue-400 ring-opacity-50 rounded-2xl z-10 pointer-events-none"
              initial={false}
              transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
            />
          )}
          <img
            src={option}
            alt={`Option ${idx + 1}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = `https://via.placeholder.com/150/3B82F6/FFFFFF?text=Image+${idx + 1}`;
            }}
          />
        </motion.button>
      );
    })}
  </div>
));

const YesNoInput = React.memo(({ value, onChange, theme }) => (
  <div className="flex gap-4">
    {['Yes', 'No'].map((option) => {
      const isSelected = value === option;
      return (
        <motion.button
          key={option}
          onClick={() => onChange(option)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex-1 px-6 py-5 border-2 rounded-xl font-semibold text-lg transition-all"
          style={{
            borderColor: isSelected ? '#3B82F6' : '#E5E7EB',
            backgroundColor: isSelected ? '#EFF6FF' : '#FFFFFF',
            color: isSelected ? '#1E40AF' : '#374151'
          }}
        >
          {option}
        </motion.button>
      );
    })}
  </div>
));

const DropdownInput = React.memo(({ options, value, onChange, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [otherValue, setOtherValue] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if current value is not in options (meaning it's an "Other" response)
  useEffect(() => {
    if (value && !options?.includes(value) && value !== 'Other') {
      setOtherValue(value);
      setShowOtherInput(true);
    }
  }, [value, options]);

  const handleSelect = (option) => {
    if (option === 'Other') {
      setShowOtherInput(true);
      setIsOpen(false);
      if (otherValue) {
        onChange(otherValue);
      }
    } else {
      setShowOtherInput(false);
      setOtherValue('');
      onChange(option);
      setIsOpen(false);
    }
  };

  const handleOtherChange = (val) => {
    setOtherValue(val);
    onChange(val);
  };

  const displayValue = showOtherInput && otherValue ? otherValue : (value === 'Other' ? '' : value);

  return (
    <div ref={dropdownRef} className="space-y-3">
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-5 py-4 border-2 rounded-xl text-base sm:text-lg transition-all duration-200 flex items-center justify-between group hover:border-blue-400"
          style={{
            borderColor: value ? '#3B82F6' : '#E5E7EB',
            backgroundColor: '#FFFFFF',
            color: value ? '#1F2937' : '#9CA3AF'
          }}
        >
          <span className={value ? 'text-gray-900' : 'text-gray-400'}>
            {displayValue || 'Select an option...'}
          </span>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown size={20} className={value ? 'text-blue-600' : 'text-gray-400'} />
          </motion.div>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute z-[9999] w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl overflow-hidden"
            >
              <div className="max-h-60 overflow-y-auto">
                {options?.map((option, idx) => {
                  const isSelected = showOtherInput && option === 'Other' ? true : value === option;
                  return (
                    <motion.button
                      key={idx}
                      type="button"
                      onClick={() => handleSelect(option)}
                      whileHover={{ backgroundColor: '#EFF6FF', x: 4 }}
                      className="w-full px-5 py-3 text-left flex items-center justify-between transition-colors text-base sm:text-lg border-b border-gray-100 last:border-b-0"
                      style={{
                        backgroundColor: isSelected ? '#EFF6FF' : '#FFFFFF',
                        color: isSelected ? '#1E40AF' : '#374151'
                      }}
                    >
                      <span className="font-medium">{option}</span>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        >
                          <Check size={18} className="text-blue-600" />
                        </motion.div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showOtherInput && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <input
            type="text"
            value={otherValue}
            onChange={(e) => handleOtherChange(e.target.value)}
            placeholder="Please specify..."
            autoFocus
            className="w-full px-5 py-4 border-2 border-blue-400 rounded-xl focus:outline-none text-base sm:text-lg transition-all focus:ring-4 focus:ring-blue-100"
            style={{
              backgroundColor: '#FFFFFF',
              color: '#1F2937'
            }}
          />
        </motion.div>
      )}
    </div>
  );
});

const GenderInput = React.memo(({ value, onChange, theme }) => {
  const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 border-2 rounded-xl text-base sm:text-lg transition-all duration-200 flex items-center justify-between group hover:border-blue-400"
        style={{
          borderColor: value ? '#3B82F6' : '#E5E7EB',
          backgroundColor: '#FFFFFF',
          color: value ? '#1F2937' : '#9CA3AF'
        }}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {value || 'Select your gender...'}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={20} className={value ? 'text-blue-600' : 'text-gray-400'} />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-[9999] w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl overflow-hidden"
          >
            {genderOptions.map((option, idx) => (
              <motion.button
                key={idx}
                type="button"
                onClick={() => handleSelect(option)}
                whileHover={{ backgroundColor: '#EFF6FF', x: 4 }}
                className="w-full px-5 py-3 text-left flex items-center justify-between transition-colors text-base sm:text-lg border-b border-gray-100 last:border-b-0"
                style={{
                  backgroundColor: value === option ? '#EFF6FF' : '#FFFFFF',
                  color: value === option ? '#1E40AF' : '#374151'
                }}
              >
                <span className="font-medium">{option}</span>
                {value === option && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  >
                    <Check size={18} className="text-blue-600" />
                  </motion.div>
                )}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const ScaleInput = React.memo(({ value, onChange, theme }) => {
  const [currentValue, setCurrentValue] = useState(value || 5);

  return (
    <div className="space-y-4">
      <div className="text-center">
        <motion.div
          key={currentValue}
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-5xl font-bold mb-2"
          style={{ color: '#3B82F6' }}
        >
          {currentValue}
        </motion.div>
        <p className="text-sm text-gray-500">Slide to rate</p>
      </div>
      <input
        type="range"
        min="1"
        max="10"
        value={currentValue}
        onChange={(e) => {
          const val = parseInt(e.target.value);
          setCurrentValue(val);
          onChange(val);
        }}
        className="w-full h-3 rounded-lg appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${((currentValue - 1) / 9) * 100}%, #E5E7EB ${((currentValue - 1) / 9) * 100}%, #E5E7EB 100%)`
        }}
      />
      <div className="flex justify-between text-xs text-gray-400 px-1">
        <span>1</span>
        <span>10</span>
      </div>
    </div>
  );
});

// Sortable item component for dnd-kit
const SortableRankingItem = ({ id, item, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-move transition-all touch-none ${
        isDragging
          ? 'border-blue-500 bg-blue-50 z-50'
          : 'bg-white hover:border-blue-400 border-gray-200'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
          style={{ backgroundColor: '#EFF6FF', color: '#3B82F6' }}
        >
          {index + 1}
        </div>
        <div className="text-gray-400">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="4" cy="4" r="1.5"/>
            <circle cx="12" cy="4" r="1.5"/>
            <circle cx="4" cy="8" r="1.5"/>
            <circle cx="12" cy="8" r="1.5"/>
            <circle cx="4" cy="12" r="1.5"/>
            <circle cx="12" cy="12" r="1.5"/>
          </svg>
        </div>
      </div>
      <span className="flex-1 font-medium text-base sm:text-lg" style={{ color: '#374151' }}>
        {item}
      </span>
    </div>
  );
};

const RankingInput = React.memo(({ options, value, onChange, theme }) => {
  const [rankedItems, setRankedItems] = useState(value || options || []);

  // Initialize rankedItems when options change
  useEffect(() => {
    if (!value && options) {
      setRankedItems(options);
    }
  }, [options, value]);

  // Setup sensors for both mouse and touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px of movement required before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms delay before drag starts on touch
        tolerance: 8, // 8px tolerance for touch movement
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rankedItems.indexOf(active.id);
      const newIndex = rankedItems.indexOf(over.id);

      const newItems = arrayMove(rankedItems, oldIndex, newIndex);
      setRankedItems(newItems);
      onChange(newItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={rankedItems}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {rankedItems?.map((item, idx) => (
            <SortableRankingItem key={item} id={item} item={item} index={idx} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
});

const DatePickerInput = React.memo(({ value, onChange, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value || '');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const handleDateSelect = (day) => {
    const { year, month } = getDaysInMonth(currentMonth);
    const dateStr = new Date(year, month, day).toISOString().split('T')[0];
    setSelectedDate(dateStr);
    onChange(dateStr);
    setIsOpen(false);
  };

  const changeMonth = (increment) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + increment);
    setCurrentMonth(newMonth);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div ref={datePickerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 border-2 rounded-xl text-base sm:text-lg transition-all duration-200 flex items-center justify-between group hover:border-blue-400"
        style={{
          borderColor: selectedDate ? '#3B82F6' : '#E5E7EB',
          backgroundColor: '#FFFFFF',
          color: selectedDate ? '#1F2937' : '#9CA3AF'
        }}
      >
        <span className={selectedDate ? 'text-gray-900' : 'text-gray-400'}>
          {selectedDate ? formatDate(selectedDate) : 'Select a date...'}
        </span>
        <Calendar size={20} className={selectedDate ? 'text-blue-600' : 'text-gray-400'} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-[9999] w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl p-4"
          >
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft size={20} className="text-gray-600" />
              </button>
              <span className="font-semibold text-gray-900">{monthName}</span>
              <button
                type="button"
                onClick={() => changeMonth(1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight size={20} className="text-gray-600" />
              </button>
            </div>

            {/* Day Names */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day) => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: startingDayOfWeek }).map((_, idx) => (
                <div key={`empty-${idx}`} className="aspect-square" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, idx) => {
                const day = idx + 1;
                const dateStr = new Date(year, month, day).toISOString().split('T')[0];
                const isSelected = dateStr === selectedDate;
                const isToday = dateStr === new Date().toISOString().split('T')[0];

                return (
                  <motion.button
                    key={day}
                    type="button"
                    onClick={() => handleDateSelect(day)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                      isSelected
                        ? 'bg-blue-600 text-white'
                        : isToday
                        ? 'bg-blue-50 text-blue-600 border-2 border-blue-200'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    {day}
                  </motion.button>
                );
              })}
            </div>

            {/* Clear Button */}
            {selectedDate && (
              <button
                type="button"
                onClick={() => {
                  setSelectedDate('');
                  onChange('');
                  setIsOpen(false);
                }}
                className="w-full mt-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                Clear Date
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const TimePickerInput = React.memo(({ value, onChange, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(value || '');
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState('AM');
  const timePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize from value
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      const hour24 = parseInt(h);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? 'PM' : 'AM';
      setHour(String(hour12).padStart(2, '0'));
      setMinute(m);
      setPeriod(period);
      setSelectedTime(value);
    }
  }, [value]);

  const formatTime = (h, m, p) => {
    const hour24 = p === 'PM' && h !== '12' ? parseInt(h) + 12 :
                   (p === 'AM' && h === '12' ? 0 : parseInt(h));
    return `${String(hour24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const formatDisplayTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour24 = parseInt(h);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${m} ${period}`;
  };

  const handleApply = () => {
    const time = formatTime(hour, minute, period);
    setSelectedTime(time);
    onChange(time);
    setIsOpen(false);
  };

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  return (
    <div ref={timePickerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 border-2 rounded-xl text-base sm:text-lg transition-all duration-200 flex items-center justify-between group hover:border-blue-400"
        style={{
          borderColor: selectedTime ? '#3B82F6' : '#E5E7EB',
          backgroundColor: '#FFFFFF',
          color: selectedTime ? '#1F2937' : '#9CA3AF'
        }}
      >
        <span className={selectedTime ? 'text-gray-900' : 'text-gray-400'}>
          {selectedTime ? formatDisplayTime(selectedTime) : 'Select time...'}
        </span>
        <Clock size={20} className={selectedTime ? 'text-blue-600' : 'text-gray-400'} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-[9999] w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl p-4"
          >
            <div className="space-y-4">
              <div className="text-center text-sm font-medium text-gray-700 mb-2">
                Select Time
              </div>

              <div className="flex gap-2">
                <select
                  value={hour}
                  onChange={(e) => setHour(e.target.value)}
                  className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 text-center font-medium"
                >
                  {hours.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>

                <span className="flex items-center text-xl font-bold text-gray-400">:</span>

                <select
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                  className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 text-center font-medium"
                >
                  {minutes.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>

                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="w-20 px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 text-center font-medium"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex-1 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium"
                >
                  Apply
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

// Animation variants
const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 30,
      stiffness: 350,
      mass: 0.8
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.2
    }
  }
};

const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1]
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.2
    }
  }
};

const slideUpVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};

const PreviewModal = ({ isOpen, onClose, form, quests }) => {
  const [currentState, setCurrentState] = useState('WELCOME');
  const [currentQuestIndex, setCurrentQuestIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [completedQuests, setCompletedQuests] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Custom hooks
  const { 
    totalXP, 
    questXP, 
    showXPPopup, 
    currentQuestionXP, 
    addXP, 
    resetQuestXP,
    setShowXPPopup 
  } = useXPManagement();

  const {
    currentQuest,
    currentQuestion,
    isLastQuestionInQuest,
    isLastQuest
  } = useQuestProgress(quests, currentQuestIndex, currentQuestionIndex);

  // Get theme colors
  const theme = useMemo(() => 
    getThemeColors(form?.settings?.theme || 'white'), 
    [form?.settings?.theme]
  );

  // Reset when modal opens
  useEffect(() => {
    if (isOpen && form) {
      setCurrentState('WELCOME');
      setCurrentQuestIndex(0);
      setCurrentQuestionIndex(0);
      setCompletedQuests([]);
      setAnswers({});
      resetQuestXP();
      setShowXPPopup(false);
    }
  }, [isOpen, form, resetQuestXP, setShowXPPopup]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      }
      
      if (currentState === 'QUEST_QUESTIONS') {
        if (e.key === 'ArrowLeft') {
          handlePreviousQuestion();
        }
        if (e.key === 'ArrowRight' && !isNextButtonDisabled()) {
          handleNextQuestion();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, currentState, onClose]);

  // Auto-hide XP popup
  useEffect(() => {
    let timeoutId;
    
    if (showXPPopup) {
      timeoutId = setTimeout(() => {
        setShowXPPopup(false);
      }, 3000);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showXPPopup]);

  // Answer validation
  const validateAnswer = useCallback((question, answer) => {
    if (!question?.required) return true;
    
    switch (question.type) {
      case 'text':
      case 'email':
      case 'number':
      case 'phone':
      case 'date':
        return answer && answer.toString().trim().length > 0;
      
      case 'multiple-choice':
        return answer !== undefined && answer !== null;
      
      case 'checkbox':
        return Array.isArray(answer) && answer.length > 0;
      
      case 'rating':
        return answer !== undefined && answer > 0;
      
      case 'image':
        return answer !== undefined;
      
      default:
        return true;
    }
  }, []);

  const areAllRequiredAnswered = useCallback(() => {
    return currentQuest?.questions?.every(question => 
      !question.required || validateAnswer(question, answers[question.id])
    ) ?? false;
  }, [currentQuest, answers, validateAnswer]);

  const isNextButtonDisabled = useCallback(() => {
    return currentQuestion?.required && !validateAnswer(currentQuestion, answers[currentQuestion?.id]);
  }, [currentQuestion, answers, validateAnswer]);

  // Get current answer
  const getCurrentAnswer = useCallback(() => {
    return answers[currentQuestion?.id];
  }, [answers, currentQuestion]);

  // Navigation handlers
  const handleStartAdventure = useCallback(() => {
    setCurrentState('QUEST_MAP');
  }, []);

  const handleQuestClick = useCallback(async (questIndex) => {
    try {
      setIsLoading(true);
      // Only allow starting unlocked quests that aren't completed
      if (questIndex === 0 || completedQuests.includes(questIndex - 1)) {
        if (!completedQuests.includes(questIndex)) {
          setCurrentQuestIndex(questIndex);
          setCurrentQuestionIndex(0);
          resetQuestXP();
          setCurrentState('QUEST_QUESTIONS');
        }
      }
    } catch (error) {
      console.error('Error starting quest:', error);
    } finally {
      setIsLoading(false);
    }
  }, [completedQuests, resetQuestXP]);

  const handleAnswerChange = useCallback((value) => {
    if (!currentQuestion) return;

    const previousAnswer = answers[currentQuestion.id];
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: value
    };
    setAnswers(newAnswers);

    // Show XP popup if this question has points and we're answering it for the first time
    if (currentQuestion.points > 0 && !previousAnswer) {
      addXP(currentQuestion.points);
    }
  }, [currentQuestion, answers, addXP]);

  const handleNextQuestion = useCallback(() => {
    if (isLastQuestionInQuest) {
      // Check if all required questions are answered
      if (!areAllRequiredAnswered()) {
        alert('Please answer all required questions before completing this quest.');
        return;
      }

      // Complete current quest
      const newCompletedQuests = [...completedQuests, currentQuestIndex];
      setCompletedQuests(newCompletedQuests);
      
      // If it's the last quest, go to form complete
      if (isLastQuest) {
        setCurrentState('FORM_COMPLETE');
      } else {
        setCurrentState('QUEST_COMPLETE');
      }
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowXPPopup(false);
    }
  }, [
    isLastQuestionInQuest, 
    areAllRequiredAnswered, 
    completedQuests, 
    currentQuestIndex, 
    isLastQuest, 
    setShowXPPopup
  ]);

  const handlePreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowXPPopup(false);
    }
  }, [currentQuestionIndex, setShowXPPopup]);

  const handleBackToMap = useCallback(() => {
    setCurrentState('QUEST_MAP');
    setShowXPPopup(false);
  }, [setShowXPPopup]);

  const handleNextQuest = useCallback(() => {
    setCurrentState('QUEST_MAP');
  }, []);

  const handleCompleteForm = useCallback(() => {
    onClose();
  }, [onClose]);

  // Calculate XP totals
  const getCurrentQuestTotalXP = useCallback(() => {
    return currentQuest?.questions?.reduce((total, question) => 
      total + (question.points || 0), 0
    ) || 0;
  }, [currentQuest]);

  const getCurrentQuestEarnedXP = useCallback(() => {
    return currentQuest?.questions?.reduce((total, question) => {
      if (answers[question.id]) {
        return total + (question.points || 0);
      }
      return total;
    }, 0) || 0;
  }, [currentQuest, answers]);

  const getTotalPossibleFormXP = useCallback(() => {
    return quests.reduce((total, quest) => 
      total + (quest.questions?.reduce((questTotal, question) => 
        questTotal + (question.points || 0), 0) || 0), 0
    );
  }, [quests]);

  // Render question input based on type
  const renderQuestionInput = useCallback((question) => {
    if (!question) return null;

    const currentAnswer = getCurrentAnswer();

    switch (question.type) {
      case 'text':
      case 'long-text':
        return (
          <TextInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="Type your answer here..."
            rows={4}
          />
        );

      case 'short-text':
        return (
          <TextInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="Type your answer here..."
            rows={1}
          />
        );

      case 'multiple-choice':
        return (
          <MultipleChoiceInput
            options={question.options}
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'checkbox':
        return (
          <CheckboxInput
            options={question.options}
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'dropdown':
        return (
          <DropdownInput
            options={question.options}
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'yes-no':
        return (
          <YesNoInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'rating':
        return (
          <RatingInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'scale':
        return (
          <ScaleInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'ranking':
        return (
          <RankingInput
            options={question.options}
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'email':
        return (
          <TextInput
            type="email"
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="your@email.com"
          />
        );

      case 'number':
        return (
          <TextInput
            type="number"
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="Enter a number"
          />
        );

      case 'phone':
        return (
          <TextInput
            type="tel"
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="+1 (555) 123-4567"
          />
        );

      case 'url':
        return (
          <TextInput
            type="url"
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="https://example.com"
          />
        );

      case 'date':
        return (
          <DatePickerInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'time':
        return (
          <TimePickerInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'gender':
        return (
          <GenderInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      case 'image':
        return (
          <ImageInput
            options={question.options}
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );

      default:
        return (
          <div className="text-gray-500 italic text-base sm:text-lg">
            Unsupported question type: {question.type}
          </div>
        );
    }
  }, [getCurrentAnswer, handleAnswerChange, theme]);

  // Render different states
  const renderContent = useCallback(() => {
    if (!form || quests.length === 0) return null;

    switch (currentState) {
      case 'WELCOME':
        return (
          <motion.div
            key="welcome"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center space-y-8 sm:space-y-12 px-4 sm:px-0 py-8"
          >
            <motion.div variants={staggerItem} className="space-y-4">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                className="inline-block"
              >
                <div className="px-6 py-2 bg-gradient-to-r from-blue-100 to-blue-50 border-2 border-blue-200 rounded-full">
                  <span className="text-sm font-semibold text-blue-700">Quest Mode</span>
                </div>
              </motion.div>

              <h1 className="text-3xl sm:text-5xl font-bold leading-tight bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent">
                Welcome to<br />{form.title}!
              </h1>
            </motion.div>

            <motion.div variants={staggerItem} className="max-w-2xl mx-auto space-y-6">
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                {form.description || `Embark on an epic journey through ${quests.length} exciting quests. Answer questions, earn XP, and complete your adventure!`}
              </p>

              <motion.div
                variants={staggerItem}
                className="grid grid-cols-3 gap-4 max-w-md mx-auto"
              >
                <div className="bg-white border-2 border-blue-100 rounded-2xl p-4">
                  <div className="text-2xl font-bold text-blue-600">{quests.length}</div>
                  <div className="text-xs text-gray-600 mt-1">Quests</div>
                </div>
                <div className="bg-white border-2 border-blue-100 rounded-2xl p-4">
                  <div className="text-2xl font-bold text-blue-600">{form.questions?.length || 0}</div>
                  <div className="text-xs text-gray-600 mt-1">Questions</div>
                </div>
                <div className="bg-white border-2 border-blue-100 rounded-2xl p-4">
                  <div className="text-2xl font-bold text-blue-600">{getTotalPossibleFormXP()}</div>
                  <div className="text-xs text-gray-600 mt-1">Total XP</div>
                </div>
              </motion.div>
            </motion.div>

            <motion.button
              variants={staggerItem}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleStartAdventure}
              className="px-8 py-4 sm:px-10 sm:py-5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-2xl font-bold text-lg sm:text-xl flex items-center gap-3 mx-auto border-2 border-blue-700 hover:border-blue-800 transition-all"
              disabled={isLoading}
            >
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Play size={24} fill="white" />
              </motion.div>
              {isLoading ? 'Loading...' : 'Start Adventure'}
            </motion.button>
          </motion.div>
        );

      case 'QUEST_MAP':
        return (
          <motion.div
            key="quest-map"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="space-y-8 px-4 sm:px-0 py-4"
          >
            <motion.div variants={staggerItem} className="text-center space-y-3">
              <div className="inline-block px-5 py-2 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-full mb-2">
                <span className="text-sm font-semibold text-blue-700">Choose Your Path</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent">
                Quest Map
              </h2>
              <p className="text-lg text-gray-600">
                Select a quest to continue your adventure
              </p>
            </motion.div>

            <motion.div variants={staggerItem}>
              <QuestIllustration
                quests={quests}
                currentQuestIndex={currentQuestIndex}
                completedQuests={completedQuests}
                onQuestClick={handleQuestClick}
                isInteractive={true}
              />
            </motion.div>

            {/* Enhanced Progress Summary */}
            <motion.div
              variants={staggerItem}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl border-2 border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                  <div className="flex items-center justify-between text-white">
                    <span className="font-semibold">Your Progress</span>
                    <span className="text-sm opacity-90">
                      {completedQuests.length} / {quests.length} Complete
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-white rounded-xl border-2 border-blue-100">
                      <div className="text-3xl font-bold text-blue-600">{totalXP}</div>
                      <div className="text-sm text-gray-600 mt-1">XP Earned</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl border-2 border-blue-100">
                      <div className="text-3xl font-bold text-blue-600">
                        {Math.round((completedQuests.length / quests.length) * 100)}%
                      </div>
                      <div className="text-sm text-gray-600 mt-1">Completed</div>
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                        initial={{ width: 0 }}
                        animate={{ width: `${(completedQuests.length / quests.length) * 100}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        );

      case 'QUEST_QUESTIONS':
        if (!currentQuest || !currentQuestion) {
          return (
            <div className="text-center text-red-500">
              Error: Quest or question not found
            </div>
          );
        }

        const progressPercentage = ((currentQuestionIndex + 1) / currentQuest.questions.length) * 100;

        return (
          <motion.div
            key="quest-questions"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="max-w-2xl mx-auto space-y-4 sm:space-y-6 px-4 sm:px-0"
          >
            {/* Enhanced Progress Bar */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                    Question {currentQuestionIndex + 1}/{currentQuest.questions.length}
                  </div>
                </div>
                <span className="text-sm font-semibold text-blue-600">
                  {Math.round(progressPercentage)}%
                </span>
              </div>
              <div className="h-3 rounded-full overflow-hidden bg-gray-200 border border-gray-300">
                <motion.div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                />
              </div>
            </motion.div>

            {/* Enhanced Question Card */}
            <motion.div
              key={currentQuestion.id}
              variants={slideUpVariants}
              initial="hidden"
              animate="visible"
              className="bg-white rounded-2xl sm:rounded-3xl border-2 border-blue-100 p-6 sm:p-8 lg:p-10 relative"
            >
              {/* Decorative gradient background */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-50 to-transparent rounded-full -mr-32 -mt-32 opacity-50 overflow-hidden" />

              {/* XP Popup */}
              <XPPopup
                xp={currentQuestionXP}
                isVisible={showXPPopup}
              />

              {/* Question Header */}
              <div className="relative z-10 mb-6 sm:mb-8">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <motion.h3
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-2xl sm:text-3xl font-bold mb-3 leading-tight text-gray-900"
                    >
                      {currentQuestion.text}
                    </motion.h3>
                    <div className="flex items-center gap-2">
                      {currentQuestion.required && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 border border-red-200 text-red-600 rounded-full text-xs font-semibold"
                        >
                          <span>Required</span>
                        </motion.div>
                      )}
                      {currentQuestion.points > 0 && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.1 }}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 border border-blue-200 text-blue-600 rounded-full text-xs font-semibold"
                        >
                          <span>+{currentQuestion.points} XP</span>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Question Input Area */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="relative z-10 space-y-4 mb-6"
              >
                {renderQuestionInput(currentQuestion)}
              </motion.div>

              {/* Pro Tip */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="relative z-0 p-4 sm:p-5 rounded-xl bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    💡
                  </div>
                  <div className="flex-1">
                    <span className="font-semibold text-blue-900 text-sm sm:text-base">Pro tip: </span>
                    <span className="text-blue-800 text-sm sm:text-base">
                      {currentQuestion.points > 0 ? `Earn ${currentQuestion.points} XP for answering this question!` :
                       currentQuestion.type === 'multiple-choice' ? 'Choose the best option that applies!' :
                       currentQuestion.type === 'checkbox' ? 'Select all options that apply!' :
                       currentQuestion.type === 'rating' ? 'Rate based on your experience!' :
                       'Complete this question to unlock the next one!'}
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Enhanced Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex justify-between items-center pt-6 gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05, x: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={currentQuestionIndex === 0 ? handleBackToMap : handlePreviousQuestion}
                className="px-6 py-3 rounded-xl bg-white border-2 border-gray-300 text-gray-700 font-semibold flex items-center gap-2 text-sm sm:text-base hover:border-gray-400 hover:bg-gray-50 transition-all"
                disabled={isLoading}
              >
                <ChevronLeft size={20} />
                <span className="hidden sm:inline">
                  {currentQuestionIndex === 0 ? 'Back to Map' : 'Previous'}
                </span>
                <span className="sm:hidden">Back</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: isNextButtonDisabled() ? 1 : 1.05, x: isNextButtonDisabled() ? 0 : 2 }}
                whileTap={{ scale: isNextButtonDisabled() ? 1 : 0.95 }}
                onClick={handleNextQuestion}
                disabled={isNextButtonDisabled() || isLoading}
                className="px-6 py-3 sm:px-8 sm:py-4 text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500 font-bold flex items-center gap-2 text-sm sm:text-base border-2 border-blue-700 hover:border-blue-800 transition-all"
              >
                <span>{isLastQuestionInQuest ? 'Complete Quest' : 'Continue'}</span>
                <ChevronRight size={20} />
              </motion.button>
            </motion.div>
          </motion.div>
        );

      case 'QUEST_COMPLETE':
        const earnedXP = getCurrentQuestEarnedXP();
        const totalPossibleXP = getCurrentQuestTotalXP();

        return (
          <motion.div
            key="quest-complete"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center space-y-10 px-4 sm:px-0 py-8"
          >
            {/* Celebration Animation */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", duration: 0.8, bounce: 0.5 }}
              className="text-7xl sm:text-8xl"
            >
              🎉
            </motion.div>

            {/* Quest Complete Card */}
            <motion.div
              variants={staggerItem}
              className="bg-gradient-to-br from-white to-blue-50 rounded-3xl p-8 sm:p-10 border-2 border-blue-200 max-w-lg mx-auto"
            >
              <div className="space-y-8">
                <div>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent mb-3"
                  >
                    Quest Complete!
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-gray-700 text-xl font-semibold"
                  >
                    {currentQuest?.title}
                  </motion.p>
                </div>

                {/* Enhanced XP Breakdown */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="grid grid-cols-2 gap-4 text-center"
                >
                  <div className="bg-white rounded-2xl p-6 border-2 border-blue-300 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-50" />
                    <div className="relative z-10">
                      <div className="text-4xl font-black text-blue-600 mb-1">{earnedXP}</div>
                      <div className="text-sm text-blue-800 font-semibold">XP Earned</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 border-2 border-blue-200 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-30" />
                    <div className="relative z-10">
                      <div className="text-4xl font-black text-gray-600 mb-1">{totalPossibleXP}</div>
                      <div className="text-sm text-gray-700 font-semibold">Total XP</div>
                    </div>
                  </div>
                </motion.div>

                {/* Progress percentage */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="text-center"
                >
                  <div className="text-sm text-gray-600 mb-2">Quest Progress</div>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                      initial={{ width: 0 }}
                      animate={{ width: `${totalPossibleXP > 0 ? (earnedXP / totalPossibleXP) * 100 : 100}%` }}
                      transition={{ duration: 1, delay: 0.7, ease: "easeOut" }}
                    />
                  </div>
                  <div className="text-2xl font-bold text-blue-600 mt-2">
                    {totalPossibleXP > 0 ? Math.round((earnedXP / totalPossibleXP) * 100) : 100}%
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Next Quest Button */}
            <motion.button
              variants={staggerItem}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNextQuest}
              className="px-10 py-5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-2xl font-bold text-xl flex items-center gap-3 mx-auto border-2 border-blue-700 hover:border-blue-800 transition-all"
              disabled={isLoading}
            >
              <span>{isLastQuest ? 'View Final Results' : 'Continue to Quest Map'}</span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ChevronRight size={24} />
              </motion.div>
            </motion.button>
          </motion.div>
        );

      case 'FORM_COMPLETE':
        const totalPossibleFormXP = getTotalPossibleFormXP();
        const formCompletionPercentage = totalPossibleFormXP > 0 ? (totalXP / totalPossibleFormXP) * 100 : 100;

        return (
          <motion.div
            key="form-complete"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center space-y-10 px-4 sm:px-0 py-8"
          >
            {/* Epic Celebration */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", duration: 1.2, bounce: 0.6 }}
              className="text-8xl sm:text-9xl"
            >
              🏆
            </motion.div>

            <motion.div
              variants={staggerItem}
              className="bg-gradient-to-br from-white via-blue-50 to-white rounded-3xl p-10 border-2 border-blue-300 max-w-2xl mx-auto"
            >
              <div className="space-y-10">
                <div className="space-y-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", bounce: 0.5 }}
                    className="inline-block"
                  >
                    <div className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full font-bold text-sm">
                      ADVENTURE COMPLETE
                    </div>
                  </motion.div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-blue-600 via-blue-800 to-blue-600 bg-clip-text text-transparent leading-tight"
                  >
                    Amazing Achievement!
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="text-2xl text-gray-700 font-semibold"
                  >
                    {form.title}
                  </motion.p>
                </div>

                {/* Epic XP Display */}
             

                {/* Stats Grid */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 }}
                  className="grid grid-cols-3 gap-4"
                >
                  <div className="bg-white rounded-2xl p-5 border-2 border-blue-200">
                    <div className="text-3xl font-black text-blue-600">{quests.length}</div>
                    <div className="text-xs text-gray-700 font-semibold mt-1">Quests</div>
                  </div>
                  <div className="bg-white rounded-2xl p-5 border-2 border-blue-200">
                    <div className="text-3xl font-black text-blue-600">{form.questions?.length || 0}</div>
                    <div className="text-xs text-gray-700 font-semibold mt-1">Questions</div>
                  </div>
                  <div className="bg-white rounded-2xl p-5 border-2 border-blue-200">
                    <div className="text-3xl font-black text-blue-600">100%</div>
                    <div className="text-xs text-gray-700 font-semibold mt-1">Complete</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Return Button */}
            <motion.button
              variants={staggerItem}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCompleteForm}
              className="px-10 py-5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-2xl font-bold text-xl border-2 border-blue-700 hover:border-blue-800 transition-all"
            >
              Return to Builder
            </motion.button>
          </motion.div>
        );

      default:
        return (
          <div className="text-center text-red-500">
            Unknown state: {currentState}
          </div>
        );
    }
  }, [
    form,
    quests,
    currentState,
    theme,
    handleStartAdventure,
    isLoading,
    handleQuestClick,
    currentQuestIndex,
    completedQuests,
    totalXP,
    currentQuest,
    currentQuestion,
    currentQuestionIndex,
    showXPPopup,
    currentQuestionXP,
    renderQuestionInput,
    handleBackToMap,
    handlePreviousQuestion,
    isNextButtonDisabled,
    handleNextQuestion,
    isLastQuestionInQuest,
    getCurrentQuestEarnedXP,
    getCurrentQuestTotalXP,
    handleNextQuest,
    isLastQuest,
    getTotalPossibleFormXP,
    handleCompleteForm
  ]);

  if (!isOpen || !form || quests.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 backdrop-blur-lg"
            onClick={onClose}
            role="presentation"
            aria-hidden="true"
          />

          {/* Modal */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 lg:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="preview-modal-title"
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="rounded-xl sm:rounded-2xl lg:rounded-3xl border-2 border-gray-300 w-full max-w-full sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl h-full max-h-screen sm:max-h-[92vh] lg:max-h-[95vh] overflow-hidden flex flex-col bg-white shadow-2xl"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-5 border-b-2 border-blue-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        rotate: currentState === 'QUEST_QUESTIONS' ? [0, 360] : 0
                      }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                      className="w-3 h-3 rounded-full border border-white/50"
                      style={{
                        backgroundColor: currentState === 'WELCOME' ? '#93C5FD' :
                                       currentState === 'QUEST_MAP' ? '#60A5FA' :
                                       currentState === 'QUEST_QUESTIONS' ? '#FBBF24' :
                                       currentState === 'QUEST_COMPLETE' ? '#34D399' :
                                       currentState === 'FORM_COMPLETE' ? '#34D399' : '#93C5FD'
                      }}
                    />
                    <span className="text-sm sm:text-base font-semibold text-white capitalize">
                      {currentState === 'QUEST_QUESTIONS' ? `${currentQuest?.title}` :
                       currentState === 'QUEST_COMPLETE' ? 'Quest Complete' :
                       currentState === 'QUEST_MAP' ? 'Quest Map' :
                       currentState === 'FORM_COMPLETE' ? 'All Quests Complete' :
                       currentState.toLowerCase().replace('_', ' ')}
                    </span>
                  </div>

                  {/* Total XP Badge */}
                  {(currentState === 'QUEST_QUESTIONS' || currentState === 'QUEST_COMPLETE' || currentState === 'QUEST_MAP') && totalXP > 0 && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-bold border-2 border-gray-200"
                    >
                      <span className="text-yellow-500">⭐</span>
                      <span className="text-blue-700">{totalXP} XP</span>
                    </motion.div>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                    aria-label="Close preview"
                  >
                    <X size={20} className="text-white" />
                  </motion.button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-blue-50/30 via-white to-blue-50/30">
                <AnimatePresence mode="wait">
                  {renderContent()}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PreviewModal;